import { useQuery } from "@tanstack/react-query";
import { getProfile, StudentProfile } from "@/services/student/profile.service";
import { getAvatarUrl } from "@/lib/avatar";
import { usePrivate } from "@/contexts/PrivateContext";
import { AuthRole } from "@/services/shared/auth/auth.interfaces";

/**
 * Hook to get the current user's avatar URL.
 * Uses React Query to cache the profile data for students.
 */
export const useUserAvatar = () => {
  const { id: userId, role } = usePrivate();
  const isStudent = role === AuthRole.Student;

  const { data: profile, isLoading, error, isError, isFetching } = useQuery({
    queryKey: ["student-profile", userId],
    queryFn: () => getProfile(),
    enabled: isStudent && Boolean(userId),
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  const resolvedUserId = userId || undefined;
  const avatarUrl =
    isStudent && profile?.photoFilename
      ? getAvatarUrl(profile.photoFilename, resolvedUserId)
      : getAvatarUrl(null, resolvedUserId);

  return {
    avatarUrl,
    isLoading: isStudent ? isLoading || isFetching : false,
    error,
    isError,
    profile: profile as StudentProfile | undefined,
  };
};
